/** @module Camunda */
define([
	"underscore",
	"csi",
	"csi/locale",
	"csi/reflection",
	"csi/reflectionHints",
	"csi/service/businessObjectService",
	"csi/sessionConstants",
	"csi/state",
	"csi/task/util",
	"csi/xml",
	"csi/xml/Search",

	"./ToolbarWarning"
], function( _, csi, locale, reflection, reflectionHints, businessObjectService, sessionConstants, state, TaskUtil, xml, Search) {

	const CAMUNDA_TASK = "CamundaTask";

	let _newTaskUrls = new Set();

	locale.registerDisplayNameResolver(reflection.TYPE_TASKS, function(node) {
		return locale.type(reflection.TYPE_TASKS);
	});

	// make this attribute have the drag and drop behavior
	reflectionHints.setSortAttribute("CamundaTaskAction", "SortOrder");

	const STATE_STARTED = "CamundaActivityState:Started";
	const STATE_COMPLETED = "CamundaActivityState:Completed";
	const STATE_SUSPENDED = "CamundaActivityState:Suspended";
	const STATE_CANCELLED = "CamundaActivityState:Cancelled";
	const STATE_ERROR = "CamundaActivityState:Error";

	/**
	 * Return true if the given process instance is started, but neither completed nor cancelled
	 * @param {Node} task
	 * @return {boolean}
	 */
	function isProcessInstanceActive(node) {
		return node.ActivityState == STATE_STARTED;
	}

	/**
	 * Return true if current user can claim the given task
	 * @param {Node} task
	 * @return {boolean}
	 */
	function canClaimTask(task) {
		if (!TaskUtil.isTaskActive(task) || (task.Assignee !== csi.EMPTY_URL)) {
			return false;
		}

		if (_.contains(task.CandidateUsers, state.get("userUrl"))) {
			return true;
		}

		return xml.nodeHasRight(task, xml.RIGHT_ADMIN);
	}

	/**
	 * Return true if task definition has action settings that requires comments for given action
	 * @param {Cache} cache
	 * @param {Node} task
	 * @param {string} actionId
	 * @return {boolean|null}
	 */
	function doesTaskActionRequireComments(cache, task, actionId) {
		const definition = cache.get(task.TaskDefinition);
		const actionSettings = cache.get(definition.ActionSettings[actionId]);
		return actionSettings && actionSettings.CommentsRequired;
	}

	/**
	 * Return true if type/class allows workflow
	 * @param {ReflectionClass} [rc]
	 * @return {boolean}
	 */
	function isValidClass(rc) {
		return businessObjectService.isConcreteAdvancedWorkflowHolder(rc);
	}

	/**
	 * Return true if type allows workflow
	 * @param {string} type
	 * @return {boolean}
	 */
	function isValidType(type) {
		return isValidClass(reflection.getClass(type));
	}

	/**
	 * Return if task definition is a user task
	 * @param {Node} taskDefinition
	 * @return {boolean}
	 */
	function taskDefinitionIsUserTask(taskDefinition) {
		return taskDefinition.Type === "CamundaTaskType:UserTask";
	}

	/**
	 * Get non-complete CamundaTask-s where the current user is a candidate.
	 * It will include assigned tasks also
	 */
	function updateAssignedTaskCount() {
        const currentUserUrl = sessionConstants.userUrl;
		const search = new Search()
			.useCountOperation()
			.byType(CAMUNDA_TASK)
		search.getQuery()
			.RefAttr("Assignee", xml.OP_EQUALS, currentUserUrl)
			.Attr("IsRunning", xml.OP_EQUALS, true);
	
		search.execute().then((response) => {
			state.set("camundaAssignedTaskCount", ~~response.data.TotalCount);
		});
	}


	return {
		// Process trigger constants
		TRIGGER_MANUAL: "Manual",
		STATE_STARTED,
		STATE_COMPLETED,
		STATE_SUSPENDED,
		STATE_CANCELLED,
		STATE_ERROR,

		isProcessInstanceActive,
		canClaimTask,
		doesTaskActionRequireComments,
		isValidType,
		isValidClass,
		taskDefinitionIsUserTask,
		updateAssignedTaskCount,
	}
});

